import React, { useState, useMemo } from 'react';
import { Calendar, Clock, User, CheckCircle2, FileText, MessageSquare, ArrowDown, Paperclip } from 'lucide-react';

export default function TaskTab({ tasks }) {
    const [sortBy, setSortBy] = useState('duedate');
    const [showSortOptions, setShowSortOptions] = useState(false);

    // Sort tasks based on selected sort option
    const sortedTasks = useMemo(() => {
        if (!tasks || tasks.length === 0) return [];
        return [...tasks].sort((a, b) => {
            if (sortBy === 'duedate') {
                const dateA = new Date(a.StartDate || a.EndDate || a.CreatedDate || 0);
                const dateB = new Date(b.StartDate || b.EndDate || b.CreatedDate || 0);
                return dateB - dateA;
            } else {
                const dateA = new Date(a.ModifiedDate || a.CompletedOn || a.CreatedDate || 0);
                const dateB = new Date(b.ModifiedDate || b.CompletedOn || b.CreatedDate || 0);
                return dateB - dateA;
            }
        });
    }, [tasks, sortBy]);

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === '' || dateStr === '-') return '-';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const day = d.getDate();
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[d.getMonth()];
            const year = d.getFullYear();
            const hours = d.getHours();
            const mins = String(d.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const h = hours % 12 || 12;
            return `${day} ${month} ${year} ${h}:${mins} ${ampm}`;
        } catch (e) {
            return dateStr;
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        if (!status) return 'text-gray-500';
        const s = status.toLowerCase();
        if (s.includes('complete') || s.includes('done') || s.includes('converted')) return 'text-green-600';
        if (s.includes('pending') || s.includes('open') || s.includes('in progress')) return 'text-yellow-600';
        if (s.includes('overdue') || s.includes('expired') || s.includes('cancel')) return 'text-red-600';
        return 'text-blue-600';
    };

    // Parse collaborators (same pattern as AppointmentTab)
    const parseCollaborators = (raw) => {
        if (!raw) return '-';
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.startsWith('[')) {
                try {
                    const arr = JSON.parse(trimmed);
                    if (Array.isArray(arr)) {
                        const names = arr.map(o => o?.CollaboratorName || o?.Name || o?.name || '').filter(Boolean);
                        return names.length ? names.join(', ') : '-';
                    }
                } catch (e) { /* plain string fallback */ }
            }
            return trimmed || '-';
        }
        if (Array.isArray(raw)) {
            return raw.map(o => o?.CollaboratorName || o?.Name || String(o)).filter(Boolean).join(', ') || '-';
        }
        return '-';
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            {/* Header with sort options */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">Task</h3>
                <div className="relative">
                    <button
                        onClick={() => setShowSortOptions(!showSortOptions)}
                        className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-700 transition-colors"
                    >
                        <span>{sortBy === 'duedate' ? 'Due Date' : 'Modified Date'}</span>
                        <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    {showSortOptions && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3 min-w-[200px]">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                                <input
                                    type="radio"
                                    name="taskSort"
                                    checked={sortBy === 'duedate'}
                                    onChange={() => { setSortBy('duedate'); setShowSortOptions(false); }}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Due Date</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="taskSort"
                                    checked={sortBy === 'modifydate'}
                                    onChange={() => { setSortBy('modifydate'); setShowSortOptions(false); }}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Modified Date</span>
                            </label>
                        </div>
                    )}
                </div>
            </div>

            {/* Task List */}
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {sortedTasks.length === 0 ? (
                    <div className="flex items-center justify-center py-10">
                        <p className="text-gray-500 text-sm">No tasks found</p>
                    </div>
                ) : (
                    sortedTasks.map((task, idx) => {
                        const title = task.Title || task.TaskName || task.EventName || `Task ${idx + 1}`;
                        const description = task.Description || task.Remarks || '';
                        const startDate = formatDate(task.StartDate || task.EventDate || task.CreatedDate);
                        const endDate = formatDate(task.EndDate);
                        const status = task.TaskStatus || task.Status || task.OutcomeName || 'Pending';
                        const creatorName = task.CreatorName || task.CreatedBy || task.UserLogin || '-';
                        const collaborators = parseCollaborators(task.Collaboraters || task.CollaboratorName);
                        const relatedTo = task.RelatedTo || task.RelatedToProfile || '-';
                        const completedOn = task.CompletedOn || '';
                        const totalAttachments = task.TotalAttachments || 0;
                        const remarks = task.Remarks || '';

                        return (
                            <div key={idx} className="px-4 py-4 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <h4 className="text-sm font-bold text-gray-900 mb-1">{title}</h4>
                                        {description && (
                                            <p className="text-xs text-gray-500 mb-2 line-clamp-2">{description}</p>
                                        )}
                                    </div>
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusColor(status) === 'text-green-600' ? 'bg-green-100 text-green-700' :
                                            getStatusColor(status) === 'text-yellow-600' ? 'bg-yellow-100 text-yellow-700' :
                                                getStatusColor(status) === 'text-red-600' ? 'bg-red-100 text-red-700' :
                                                    'bg-blue-100 text-blue-700'
                                        }`}>
                                        {status}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" title="Start Date" />
                                        <span className="text-xs text-gray-600">{startDate}</span>
                                    </div>
                                    {endDate && endDate !== '-' && (
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" title="End Date" />
                                            <span className="text-xs text-gray-600">{endDate}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <User className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" title="Creator" />
                                        <span className="text-xs text-gray-600 truncate">{creatorName}</span>
                                    </div>
                                    {collaborators !== '-' && (
                                        <div className="flex items-center gap-2">
                                            <User className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" title="Collaborators" />
                                            <span className="text-xs text-gray-600 truncate">{collaborators}</span>
                                        </div>
                                    )}
                                    {relatedTo !== '-' && (
                                        <div className="flex items-center gap-2">
                                            <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" title="Related To" />
                                            <span className="text-xs text-gray-600 truncate">{relatedTo}</span>
                                        </div>
                                    )}
                                    {completedOn && (
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" title="Completed On" />
                                            <span className="text-xs text-gray-600">{completedOn}</span>
                                        </div>
                                    )}
                                    {Number(totalAttachments) > 0 && (
                                        <div className="flex items-center gap-2">
                                            <Paperclip className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" title="Attachments" />
                                            <span className="text-xs text-gray-600">{totalAttachments} attachment(s)</span>
                                        </div>
                                    )}
                                    {remarks && (
                                        <div className="flex items-start gap-2 col-span-2">
                                            <MessageSquare className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" title="Remarks" />
                                            <span className="text-xs text-gray-600 line-clamp-2">{remarks}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
